import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable table row wrapper - exposes drag attributes via render prop
function SortableRow({ id, disabled, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        background: isDragging ? '#f8fafc' : undefined,
    };
    return (
        <tr ref={setNodeRef} style={style} className="border-b transition-colors hover:bg-slate-50/50">
            {children({ ...attributes, ...listeners })}
        </tr>
    );
}
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
    Plus, Save, FileSpreadsheet, FileText, Trash2, RefreshCw,
    Wine, Coffee, Utensils, Waves, Umbrella, Music, Building,
    Calculator, Calendar, Euro, Banknote, Camera, Upload, Eye, X,
    ScanBarcode, Users, User, UserCheck, CreditCard, ChevronUp, ChevronDown, GripVertical, Copy, Gift, PackagePlus, AlertTriangle
} from 'lucide-react';
import BarTreats from '../components/BarTreats';
import BarConsumables from '../components/BarConsumables';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../lib/api';
import { parseLocaleNumber, formatTRNumber } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BAR_CONFIG = {
    'lobby': { name: 'Lobby Bar', icon: Coffee, color: 'bg-amber-500' },
    'servis': { name: 'Servis Bar', icon: Utensils, color: 'bg-blue-500' },
    'vitamin': { name: 'Vitamin Bar', icon: Wine, color: 'bg-green-500' },
    'havuz': { name: 'Havuz Bar', icon: Waves, color: 'bg-cyan-500' },
    'plaj': { name: 'Plaj Bar', icon: Umbrella, color: 'bg-orange-500' },
    'amfi': { name: 'Amfi Bar', icon: Music, color: 'bg-purple-500' },
    'teras': { name: 'Teras Bar', icon: Building, color: 'bg-slate-600' },
    'alacarte': { name: 'Alacarte Bar', icon: Wine, color: 'bg-rose-600' },
    'minibar': { name: 'Minibar', icon: Coffee, color: 'bg-emerald-600' },
};

export function BarStock() {
    const { barId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [stockItems, setStockItems] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [euroRate, setEuroRate] = useState(35.5);
    const [guestCount, setGuestCount] = useState(0);

    // Garson mode - read from sessionStorage
    const isGarson = user?.role === 'garson';
    const garsonName = isGarson ? (sessionStorage.getItem('garson_name') || '') : '';
    const isGarsonMode = isGarson && garsonName;
    
    // Yonetim (management) ve Personel (staff): sadece "Bugun Sayim" duzenleyebilir
    const isReadOnlyExceptToday = user?.role === 'manager' || user?.role === 'staff';
    // Diger alanlar (urun adi, fiyat, dunden kalan, cekilen) bu rollerde kilitli
    const isFieldLocked = isGarsonMode || isReadOnlyExceptToday;
    
    // Garson ve Personel (staff) fiyat sutunlarini gormez (Birim Fiyat TL, Toplam TL, Toplam EUR)
    const canSeePrices = !isGarson && user?.role !== 'staff';

    // Receipt photos
    const [receipts, setReceipts] = useState([]);
    const [uploadingReceipt, setUploadingReceipt] = useState(false);
    const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const fileInputRef = useRef(null);

    // Product autocomplete
    const [suggestions, setSuggestions] = useState([]);
    const [activeInputIndex, setActiveInputIndex] = useState(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    // Stock history
    const [showHistory, setShowHistory] = useState(false);
    const [showTreats, setShowTreats] = useState(false);
    const [showConsumables, setShowConsumables] = useState(false);
    const [stockSearchTerm, setStockSearchTerm] = useState('');
    const [historyData, setHistoryData] = useState([]);

    // Barcode scanner
    const [scannerActive, setScannerActive] = useState(false);
    const qrScannerRef = useRef(null);
    const [scannedProduct, setScannedProduct] = useState(null);
    const [scannedQty, setScannedQty] = useState('');
    const [scanDialogOpen, setScanDialogOpen] = useState(false);
    const scanCooldownRef = useRef(false);

    // Template copy (only admin - manager artik fiyat/urun ekleyemez)
    const isAdmin = user?.role === 'admin';
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [copyTargets, setCopyTargets] = useState([]);
    const [sednaGuests, setSednaGuests] = useState(null);
    const [unmatchedSedna, setUnmatchedSedna] = useState([]);
    const [copyMode, setCopyMode] = useState('overwrite');
    const [copyApplyToday, setCopyApplyToday] = useState(true);
    const [copyLoading, setCopyLoading] = useState(false);

    const barConfig = BAR_CONFIG[barId] || BAR_CONFIG['lobby'];
    const BarIcon = barConfig.icon;

    useEffect(() => {
        const loadData = async () => {
            initialLoadRef.current = true;
            await fetchTcmbRate();
            fetchStockData();
            fetchReceipts();
            fetchHistory();
        };
        loadData();
    }, [barId, selectedDate]);

    // Fetch TCMB EUR rate automatically
    const fetchTcmbRate = async () => {
        try {
            const res = await api.get('/tcmb-rate');
            if (res.data && res.data.rate) {
                setEuroRate(res.data.rate);
            }
        } catch (e) {
            console.error('TCMB rate error:', e);
        }
    };

    // Camera QR scanner
    const startScanner = useCallback(async () => {
        if (qrScannerRef.current) return;
        try {
            const html5QrCode = new Html5Qrcode("qr-reader");
            qrScannerRef.current = html5QrCode;
            await html5QrCode.start(
                { facingMode: "environment" },
                { fps: 10, qrbox: { width: 250, height: 250 } },
                async (decodedText) => {
                    // Prevent rapid re-scans while dialog is open
                    if (scanCooldownRef.current) return;
                    scanCooldownRef.current = true;

                    if (decodedText.startsWith('KAPTAN_PRODUCT:')) {
                        const parts = decodedText.split(':');
                        if (parts.length >= 3) {
                            const productId = parts[1];
                            try {
                                const res = await api.get(`/products/${productId}`);
                                const product = res.data;
                                setScannedProduct(product);
                                setScannedQty('');
                                setScanDialogOpen(true);
                            } catch {
                                toast.error('Urun bulunamadi');
                                scanCooldownRef.current = false;
                            }
                        }
                    } else {
                        toast.error('Gecersiz QR kod');
                        scanCooldownRef.current = false;
                    }
                },
                () => {}
            );
        } catch (err) {
            console.error('Scanner start error:', err);
            toast.error('Kamera acilamadi. Kamera izni verildiginden emin olun.');
            setScannerActive(false);
        }
    }, []);

    const stopScanner = useCallback(async () => {
        if (qrScannerRef.current) {
            try {
                await qrScannerRef.current.stop();
                qrScannerRef.current.clear();
            } catch (e) {
                console.error('Scanner stop error:', e);
            }
            qrScannerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (scannerActive) {
            setTimeout(() => startScanner(), 100);
        } else {
            stopScanner();
        }
        return () => { stopScanner(); };
    }, [scannerActive]);

    const confirmScannedProduct = () => {
        if (!scannedProduct) return;
        const qty = parseFloat(scannedQty) || 0;
        if (qty <= 0) {
            toast.error('Miktar giriniz');
            return;
        }

        const existingIdx = stockItems.findIndex(
            item => item.product_name.toLowerCase() === scannedProduct.name.toLowerCase()
        );

        if (existingIdx >= 0) {
            const newItems = [...stockItems];
            newItems[existingIdx].today_count = (newItems[existingIdx].today_count || 0) + qty;
            const item = newItems[existingIdx];
            item.consumption = Math.max(0, (item.yesterday_remaining || 0) + (item.received || 0) - (item.today_count || 0));
            item.total_cost_tl = item.consumption * (item.unit_price_tl || 0);
            item.total_cost_eur = euroRate > 0 ? item.total_cost_tl / euroRate : 0;
            setStockItems(newItems);
            toast.success(`${scannedProduct.name}: Sayim guncellendi (${newItems[existingIdx].today_count})`);
        } else {
            const newItem = {
                product_name: scannedProduct.name,
                unit: scannedProduct.unit || 'adet',
                yesterday_remaining: 0,
                received: 0,
                today_count: qty,
                consumption: 0,
                unit_price_tl: scannedProduct.price_tl || 0,
                total_cost_tl: 0,
                total_cost_eur: 0,
            };
            setStockItems(prev => {
                const filtered = prev.filter(i => i.product_name);
                return [...filtered, newItem];
            });
            toast.success(`${scannedProduct.name} eklendi (${qty} ${scannedProduct.unit || 'adet'})`);
        }

        setScanDialogOpen(false);
        setScannedProduct(null);
        setScannedQty('');
        scanCooldownRef.current = false;
    };

    const cancelScanDialog = () => {
        setScanDialogOpen(false);
        setScannedProduct(null);
        setScannedQty('');
        scanCooldownRef.current = false;
    };

    // Admin: track initial snapshot (product_name + unit_price_tl) to detect changes
    const initialSnapshotRef = useRef([]);
    const [adminConfirmOpen, setAdminConfirmOpen] = useState(false);
    const [adminConfirmChanges, setAdminConfirmChanges] = useState([]);
    // Reactive flag - true when admin has unsaved price/product changes (shown in UI)
    const [masterDirty, setMasterDirty] = useState(false);

    // Display helper: convert numeric value to TR-string for input display
    const toDisplay = (v) => {
        if (v === null || v === undefined || v === '') return '';
        if (typeof v === 'string') return v;  // user input preserved as-is
        const n = Number(v);
        if (!isFinite(n) || n === 0) return '';  // 0 → boş göster (placeholder "0" görünsün)
        return String(n).replace('.', ',');
    };

    const fetchStockData = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/bar-stock/${barId}`, { params: { date: selectedDate } });
            let items;
            if (res.data && res.data.items && res.data.items.length > 0) {
                items = res.data.items.map((it, i) => ({
                    ...it,
                    // Convert numeric fields to TR-string display ('2,5' instead of 2.5)
                    yesterday_remaining: toDisplay(it.yesterday_remaining),
                    received: toDisplay(it.received),
                    today_count: toDisplay(it.today_count),
                    unit_price_tl: toDisplay(it.unit_price_tl),
                    _rowId: it._rowId || `row-loaded-${i}-${Date.now()}`,
                }));
            } else {
                items = [createEmptyItem()];
            }
            setStockItems(items);
            // Snapshot prices/names for change detection (normalized to string form)
            initialSnapshotRef.current = items.map(it => ({
                product_name: (it.product_name || '').trim(),
                unit_price_tl: parseLocaleNumber(it.unit_price_tl),
            }));
            setMasterDirty(false);
            if (res.data?.guest_count !== undefined) {
                setGuestCount(res.data.guest_count || 0);
            }
        } catch {
            const items = [createEmptyItem()];
            setStockItems(items);
            initialSnapshotRef.current = items.map(() => ({ product_name: '', unit_price_tl: 0 }));
            setMasterDirty(false);
        } finally {
            setLoading(false);
        }
    };

    const fetchReceipts = async () => {
        try {
            const res = await api.get(`/bar-stock/${barId}/receipts`, { params: { date: selectedDate } });
            setReceipts(res.data || []);
        } catch (error) {
            console.error('Receipt fetch error:', error);
        }
    };

    const fetchHistory = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/bar-stock/${barId}/history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setHistoryData(await res.json());
            }
        } catch (error) {
            console.error('History fetch error:', error);
        }
    };

    const createEmptyItem = () => ({
        _rowId: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        product_name: '',
        unit: 'adet',
        yesterday_remaining: 0,
        received: 0,
        today_count: 0,
        consumption: 0,
        unit_price_tl: 0,
        total_cost_tl: 0,
        total_cost_eur: 0,
    });

    // Product autocomplete fix - click outside to close
    useEffect(() => {
        const handleClickOutside = (e) => {
            // Only close if click is NOT on an input or dropdown item
            const target = e.target;
            if (target.closest('input[data-testid^="product-name-"]')) return;
            if (target.closest('[data-autocomplete-dropdown]')) return;
            setSuggestions([]);
            setActiveInputIndex(null);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // Sedna guest counts — selectedDate'e göre dinamik (fallback yok, tarih için veri varsa gösterir)
    useEffect(() => {
        let cancelled = false;
        setSednaGuests(null);
        const loadSedna = async () => {
            try {
                const res = await fetch(`${API_URL}/api/sedna/guests/${selectedDate}`);
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled) return;
                setSednaGuests(data && data.available ? data : null);
            } catch {/* silent */}
        };
        loadSedna();
        return () => { cancelled = true; };
    }, [selectedDate, barId]);

    // Sedna unmatched (çekilen ama tanımlı olmayan) - bar/date'e göre
    const reloadUnmatched = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const res = await fetch(
                `${API_URL}/api/sedna/unmatched?bar_id=${barId}&report_date=${selectedDate}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!res.ok) { setUnmatchedSedna([]); return; }
            const data = await res.json();
            setUnmatchedSedna(data.items || []);
        } catch { setUnmatchedSedna([]); }
    };
    useEffect(() => {
        let cancelled = false;
        setUnmatchedSedna([]);
        (async () => {
            await reloadUnmatched();
            if (cancelled) setUnmatchedSedna([]);
        })();
        return () => { cancelled = true; };
    }, [selectedDate, barId]);

    const dismissUnmatched = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API_URL}/api/sedna/unmatched/${id}/dismiss`, {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
            });
            setUnmatchedSedna(prev => prev.filter(u => u.id !== id));
        } catch {/* silent */}
    };

    const dismissAllUnmatched = async () => {
        try {
            const token = localStorage.getItem('token');
            await fetch(
                `${API_URL}/api/sedna/unmatched/dismiss-all?bar_id=${barId}&report_date=${selectedDate}`,
                { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
            );
            setUnmatchedSedna([]);
        } catch {/* silent */}
    };

    // Hızlı Ekle dialog state
    const [quickAddTarget, setQuickAddTarget] = useState(null);
    const [quickAddPrice, setQuickAddPrice] = useState('');
    const [quickAddCount, setQuickAddCount] = useState('');
    const [quickAddYesterday, setQuickAddYesterday] = useState('');
    const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);

    const quickAddUnmatched = (u) => {
        setQuickAddTarget(u);
        setQuickAddPrice('');
        setQuickAddCount('');
        setQuickAddYesterday('');
    };

    const submitQuickAdd = async () => {
        if (!quickAddTarget) return;
        setQuickAddSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/sedna/unmatched/${quickAddTarget.id}/quick-add`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    unit_price_tl: parseFloat(quickAddPrice) || 0,
                    today_count: parseFloat(quickAddCount) || 0,
                    yesterday_remaining: parseFloat(quickAddYesterday) || 0,
                    unit: quickAddTarget.unit,
                    product_name: quickAddTarget.product_name,
                }),
            });
            if (!res.ok) {
                toast.error('Hızlı ekle başarısız');
            } else {
                toast.success(`${quickAddTarget.product_name} bara eklendi`);
                setUnmatchedSedna(prev => prev.filter(u => u.id !== quickAddTarget.id));
                setQuickAddTarget(null);
                // Bar stok ekranını yenile
                await fetchStockData();
            }
        } catch {
            toast.error('Hata oluştu');
        } finally {
            setQuickAddSubmitting(false);
        }
    };

    const handleProductSearch = async (value, index, inputEl = null) => {
        setActiveInputIndex(index);
        if (inputEl) {
            const rect = inputEl.getBoundingClientRect();
            // position: fixed uses viewport coords, no scroll offset needed
            const dropdownHeight = 240; // max-h-60
            const spaceBelow = window.innerHeight - rect.bottom;
            const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
            setDropdownPos({
                top: showAbove ? rect.top - dropdownHeight - 2 : rect.bottom + 2,
                left: rect.left,
                width: rect.width
            });
        }
        const newItems = [...stockItems];
        const oldName = (initialSnapshotRef.current[index]?.product_name || '').trim();
        newItems[index].product_name = value;
        // Mark admin dirty if product_name diverges from initial snapshot
        if (isAdmin && value.trim() !== oldName) setMasterDirty(true);
        setStockItems(newItems);

        if (value.length >= 1) {
            try {
                const res = await api.get('/products/autocomplete', { params: { q: value } });
                setSuggestions(res.data || []);
            } catch {
                setSuggestions([]);
            }
        } else {
            setSuggestions([]);
        }
    };

    const selectProduct = (index, product) => {
        const newItems = [...stockItems];
        // Duplicate prevention: same product_name already in another row?
        const normalizedNew = (product.name || '').trim().toLocaleLowerCase('tr');
        const isDuplicate = newItems.some((it, i) => i !== index && (it.product_name || '').trim().toLocaleLowerCase('tr') === normalizedNew);
        if (isDuplicate) {
            toast.error(`"${product.name}" zaten listede mevcut`);
            setSuggestions([]);
            setActiveInputIndex(null);
            return;
        }
        newItems[index].product_name = product.name;
        newItems[index].unit = product.unit;
        newItems[index].unit_price_tl = product.price_tl || 0;
        const item = newItems[index];
        const yr = parseLocaleNumber(item.yesterday_remaining);
        const rc = parseLocaleNumber(item.received);
        const tc = parseLocaleNumber(item.today_count);
        const up = parseLocaleNumber(item.unit_price_tl);
        // v7.2: today_count VE received her ikisi de 0 → henüz sayılmamış → cons=0
        const rawCons = (tc === 0 && rc === 0) ? 0 : Math.max(0, yr + rc - tc);
        item.consumption = Math.round(rawCons * 100) / 100;
        item.total_cost_tl = Math.round(item.consumption * up * 100) / 100;
        item.total_cost_eur = euroRate > 0 ? Math.round((item.total_cost_tl / euroRate) * 100) / 100 : 0;
        setStockItems(newItems);
        setSuggestions([]);
        setActiveInputIndex(null);
    };

    const updateItem = (index, field, value) => {
        const newItems = [...stockItems];
        newItems[index][field] = value;
        if (isAdmin && field === 'unit_price_tl') {
            const oldPrice = parseLocaleNumber(initialSnapshotRef.current[index]?.unit_price_tl);
            const newPrice = parseLocaleNumber(value);
            if (Math.abs(newPrice - oldPrice) > 0.001) setMasterDirty(true);
        }
        const item = newItems[index];
        const yr = parseLocaleNumber(item.yesterday_remaining);
        const rc = parseLocaleNumber(item.received);
        const tc = parseLocaleNumber(item.today_count);
        const up = parseLocaleNumber(item.unit_price_tl);
        // v7.2: today_count VE received her ikisi de 0 → henüz sayılmamış → cons=0
        const rawCons = (tc === 0 && rc === 0) ? 0 : Math.max(0, yr + rc - tc);
        item.consumption = Math.round(rawCons * 100) / 100;
        item.total_cost_tl = Math.round(item.consumption * up * 100) / 100;
        item.total_cost_eur = euroRate > 0 ? Math.round((item.total_cost_tl / euroRate) * 100) / 100 : 0;
        setStockItems(newItems);
    };

    const addNewProduct = () => {
        setStockItems([...stockItems, createEmptyItem()]);
    };

    const removeProduct = (index) => {
        if (stockItems.length === 1) {
            toast.error('En az bir urun satiri olmali');
            return;
        }
        setStockItems(stockItems.filter((_, i) => i !== index));
    };

    const moveProduct = (index, direction) => {
        // direction: -1 = up, +1 = down
        const target = index + direction;
        if (target < 0 || target >= stockItems.length) return;
        const next = [...stockItems];
        [next[index], next[target]] = [next[target], next[index]];
        setStockItems(next);
    };

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = stockItems.findIndex((it, i) => (it._rowId || `row-${i}`) === active.id);
        const newIndex = stockItems.findIndex((it, i) => (it._rowId || `row-${i}`) === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        setStockItems(arrayMove(stockItems, oldIndex, newIndex));
    };

    // Detect master-data changes: NAME-based lookup (order-independent) so that
    // reordering rows via drag-and-drop or up/down arrows does NOT count as a
    // "master data change" — otherwise auto-save gets blocked for admin.
    // Only report:
    //   • ADDED: a name exists now but not in snapshot
    //   • REMOVED: a name existed in snapshot but not now
    //   • PRICE_CHANGED: same name, unit_price_tl differs
    const detectMasterChanges = () => {
        const snap = initialSnapshotRef.current || [];
        // Build maps of name → price (case-insensitive) — empty names skipped
        const snapByName = new Map();
        snap.forEach(s => {
            const n = (s.product_name || '').trim().toLowerCase();
            if (n) snapByName.set(n, parseLocaleNumber(s.unit_price_tl) || 0);
        });
        const currentByName = new Map();
        stockItems.forEach((it, i) => {
            const n = (it.product_name || '').trim().toLowerCase();
            if (n) currentByName.set(n, { price: parseLocaleNumber(it.unit_price_tl) || 0, index: i, display: (it.product_name || '').trim() });
        });
        const changes = [];
        // ADDED — in current but not in snap
        for (const [name, info] of currentByName.entries()) {
            if (!snapByName.has(name)) {
                changes.push({ index: info.index, type: 'name', oldVal: '(boş)', newVal: info.display, product: info.display });
            } else {
                // PRICE CHANGED — same name, different price
                const oldPrice = snapByName.get(name);
                if (Math.abs(oldPrice - info.price) > 0.001) {
                    changes.push({ index: info.index, type: 'price', oldVal: oldPrice.toFixed(2), newVal: info.price.toFixed(2), product: info.display });
                }
            }
        }
        // REMOVED — in snap but not in current
        for (const [name, oldPrice] of snapByName.entries()) {
            if (!currentByName.has(name)) {
                changes.push({ index: -1, type: 'name', oldVal: name, newVal: '(silindi)', product: name, oldPrice });
            }
        }
        return changes;
    };

    const doSave = async (silent = false) => {
        try {
            setSaving(true);
            // Parse all numeric strings before sending (backend also re-parses, but keep payload clean)
            const cleanItems = stockItems.map(it => ({
                ...it,
                yesterday_remaining: parseLocaleNumber(it.yesterday_remaining),
                received: parseLocaleNumber(it.received),
                today_count: parseLocaleNumber(it.today_count),
                unit_price_tl: parseLocaleNumber(it.unit_price_tl),
                request_qty: parseLocaleNumber(it.request_qty),
                request_sent_at: it.request_sent_at || null,
            }));
            await api.post(`/bar-stock/${barId}`, {
                date: selectedDate,
                euro_rate: euroRate,
                items: cleanItems,
                guest_count: guestCount > 0 ? guestCount : null,
            });

            // Auto-forward pending requests to bar_requests (Sedna F&B Talep Fişi)
            const TALEP_ENABLED = true;
            const pendingReqs = TALEP_ENABLED ? cleanItems.filter(it =>
                it.product_name && it.product_name.trim() &&
                (Number(it.request_qty) || 0) > 0 && !it.request_sent_at
            ) : [];
            if (pendingReqs.length > 0) {
                try {
                    await api.post('/bar-requests', {
                        bar_id: barId,
                        date: selectedDate,
                        items: pendingReqs.map(it => ({
                            product_name: it.product_name.trim(),
                            unit: it.unit || 'adet',
                            quantity: Number(it.request_qty) || 0,
                        })),
                    });
                    // Mark those rows as sent (persist to same doc so it survives reload)
                    const nowIso = new Date().toISOString();
                    const sentNames = new Set(pendingReqs.map(x => x.product_name.trim()));
                    const stampedItems = cleanItems.map(it => (
                        sentNames.has((it.product_name || '').trim()) && (Number(it.request_qty) || 0) > 0
                            ? { ...it, request_sent_at: nowIso }
                            : it
                    ));
                    setStockItems(prev => prev.map((it) => {
                        const name = (it.product_name || '').trim();
                        if (sentNames.has(name) && (parseLocaleNumber(it.request_qty) || 0) > 0) {
                            return { ...it, request_sent_at: nowIso };
                        }
                        return it;
                    }));
                    // Persist request_sent_at flag by re-saving stock silently
                    await api.post(`/bar-stock/${barId}`, {
                        date: selectedDate,
                        euro_rate: euroRate,
                        items: stampedItems,
                        guest_count: guestCount > 0 ? guestCount : null,
                    });
                    if (!silent) toast.success(`Stok kaydedildi + ${pendingReqs.length} talep gönderildi`);
                } catch (err) {
                    if (!silent) toast.error('Stok kaydedildi ancak talep gönderilemedi');
                }
            } else if (!silent) {
                toast.success('Stok kaydedildi');
            }

            // Update snapshot to current values (changes committed) and clear dirty flag
            initialSnapshotRef.current = stockItems.map(it => ({
                product_name: (it.product_name || '').trim(),
                unit_price_tl: parseLocaleNumber(it.unit_price_tl),
            }));
            setMasterDirty(false);
        } catch {
            if (!silent) toast.error('Kaydetme basarisiz');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async (silent = false) => {
        // Admin explicit save: fiyat/silme onayı iste. Yeni ürün ekleme onay gerektirmez.
        if (!silent && isAdmin) {
            const changes = detectMasterChanges();
            const blocking = changes.filter(c =>
                c.type === 'price' || (c.type === 'name' && c.newVal === '(silindi)')
            );
            if (blocking.length > 0 || masterDirty) {
                setAdminConfirmChanges(blocking.length > 0 ? blocking : changes);
                setAdminConfirmOpen(true);
                return;
            }
        }
        await doSave(silent);
    };

    // Auto-save: persist any change after 1.5s idle (skip during initial load).
    // v7.2: Admin için sadece TEHLİKELİ master değişiklikler auto-save'i bloklar:
    //   - Fiyat değişikliği (yanlış fiyata kaydetmeyi önlemek için onay şart)
    //   - Ürün SİLME (yanlış silmeyi önlemek için onay şart)
    // Yeni ürün EKLEME ise güvenli — miktarları hemen kaydedilsin (Algida bug fix).
    const autoSaveTimer = useRef(null);
    const initialLoadRef = useRef(true);
    useEffect(() => {
        if (loading) return;
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            return;
        }
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        // Yalnızca fiyat değişikliği veya silme varsa auto-save'i durdur
        if (isAdmin) {
            if (masterDirty) return; // manuel fiyat değişikliği bayrağı
            const changes = detectMasterChanges();
            const hasBlockingChange = changes.some(c =>
                c.type === 'price' || (c.type === 'name' && c.newVal === '(silindi)')
            );
            if (hasBlockingChange) return;
        }
        autoSaveTimer.current = setTimeout(() => {
            doSave(true);
        }, 1500);
        return () => {
            if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stockItems, masterDirty]);

    const handleReceiptUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            setUploadingReceipt(true);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('date', selectedDate);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/bar-stock/${barId}/receipt`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            if (res.ok) {
                toast.success('Fis fotografi yuklendi');
                fetchReceipts();
            } else {
                toast.error('Yukleme basarisiz');
            }
        } catch {
            toast.error('Yukleme sirasinda hata olustu');
        } finally {
            setUploadingReceipt(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const deleteReceipt = async (receiptId) => {
        if (!window.confirm('Bu fis fotografini silmek istediginize emin misiniz?')) return;
        try {
            await api.delete(`/bar-receipts/${receiptId}`);
            toast.success('Fis silindi');
            fetchReceipts();
            setReceiptDialogOpen(false);
        } catch {
            toast.error('Silme basarisiz');
        }
    };

    const exportToExcel = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/bar-stock/${barId}/export/excel?date=${selectedDate}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${barConfig.name.replace(' ', '_')}_${selectedDate}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('Excel dosyasi indirildi');
            } else {
                toast.error('Excel export basarisiz');
            }
        } catch {
            toast.error('Export sirasinda hata olustu');
        }
    };

    const handleCopyTemplate = async () => {
        if (copyTargets.length === 0) {
            toast.error('En az bir hedef bar secin');
            return;
        }
        setCopyLoading(true);
        try {
            const res = await api.post('/bar-templates/copy', {
                source_bar_id: barId,
                target_bar_ids: copyTargets,
                mode: copyMode,
                apply_today: copyApplyToday,
            });
            toast.success(res.data.message || 'Liste kopyalandi');
            setCopyDialogOpen(false);
            setCopyTargets([]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Kopyalama basarisiz');
        } finally {
            setCopyLoading(false);
        }
    };

    const exportToPDF = async () => {        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/bar-stock/${barId}/export/pdf?date=${selectedDate}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${barConfig.name.replace(' ', '_')}_${selectedDate}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                toast.success('PDF dosyasi indirildi');
            } else {
                toast.error('PDF export basarisiz');
            }
        } catch {
            toast.error('Export sirasinda hata olustu');
        }
    };

    const totalTL = stockItems.reduce((sum, item) => sum + (item.total_cost_tl || 0), 0);
    const totalEUR = stockItems.reduce((sum, item) => sum + (item.total_cost_eur || 0), 0);
    const totalConsumption = stockItems.reduce((sum, item) => sum + (item.consumption || 0), 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-orwion-green" />
            </div>
        );
    }

    // Mobile card for each stock item
    const MobileStockCard = ({ item, index }) => (
        <Card className="border-l-4 border-l-slate-300">
            <CardContent className="p-3 space-y-3">
                {/* Product name row */}
                <div className="relative">
                    <Input
                        value={item.product_name}
                        onChange={(e) => handleProductSearch(e.target.value, index)}
                        onFocus={() => setActiveInputIndex(index)}
                        placeholder="Urun adi yazin..."
                        className="h-10 text-sm font-medium"
                        data-testid={`mobile-product-name-${index}`}
                    />
                    {activeInputIndex === index && suggestions.length > 0 && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                            {suggestions.map((product) => (
                                <div
                                    key={product.id}
                                    className="px-3 py-2.5 hover:bg-slate-100 cursor-pointer flex justify-between items-center"
                                    onMouseDown={(e) => { e.preventDefault(); selectProduct(index, product); }}
                                >
                                    <span className="text-sm">{product.name}</span>
                                    <Badge variant="outline" className="text-xs">{product.unit}</Badge>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Unit + Price row */}
                <div className="flex items-center gap-2 text-xs text-slate-500">
                    <Badge variant="outline" className="uppercase text-xs">{item.unit || 'adet'}</Badge>
                    <span>Birim: {formatTRNumber(parseLocaleNumber(item.unit_price_tl), 2)} TL</span>
                    <Button variant="ghost" size="icon" className="ml-auto h-7 w-7 text-red-500" onClick={() => removeProduct(index)}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                </div>

                {/* Numbers grid */}
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block">Dunden Kalan</label>
                        <Input type="text" inputMode="decimal" value={item.yesterday_remaining ?? ''} onChange={(e) => updateItem(index, 'yesterday_remaining', e.target.value)}
                            onFocus={(e) => e.target.select()} placeholder="0"
                            className="h-9 text-center text-sm" data-testid={`mobile-yesterday-${index}`} />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block">Cekilen Urun</label>
                        <Input type="text" inputMode="decimal" value={item.received ?? ''} onChange={(e) => updateItem(index, 'received', e.target.value)}
                            onFocus={(e) => e.target.select()} placeholder="0"
                            className="h-9 text-center text-sm" data-testid={`mobile-received-${index}`} />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block">Bugun Sayim</label>
                        <Input type="text" inputMode="decimal" value={item.today_count ?? ''} onChange={(e) => updateItem(index, 'today_count', e.target.value)}
                            onFocus={(e) => e.target.select()} placeholder="0"
                            className="h-9 text-center text-sm bg-blue-50 border-blue-200" data-testid={`mobile-today-${index}`} />
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block">Birim Fiyat (TL)</label>
                        <Input type="text" inputMode="decimal" value={item.unit_price_tl ?? ''} onChange={(e) => updateItem(index, 'unit_price_tl', e.target.value)}
                            onFocus={(e) => e.target.select()} placeholder="0"
                            className={`h-9 text-center text-sm ${(parseLocaleNumber(item.unit_price_tl) === 0 && parseLocaleNumber(item.consumption) > 0) ? 'border-red-400 bg-red-50' : ''}`}
                            data-testid={`mobile-unit-price-${index}`} />
                    </div>
                    <div className="col-span-2">
                        <label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                            Talep {item.request_sent_at && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[9px] px-1 h-4">✓ Gönderildi</Badge>}
                        </label>
                        <Input type="text" inputMode="decimal" value={item.request_qty ?? ''}
                            onChange={(e) => updateItem(index, 'request_qty', e.target.value)}
                            onFocus={(e) => e.target.select()}
                            placeholder="0" className="h-9 text-center text-sm" data-testid={`mobile-request-qty-${index}`}
                            title="Kaydettiğinizde Talepler sekmesine ve Sedna'ya iletilir" />
                    </div>
                </div>

                {/* Calculated values */}
                <div className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-sm">
                    <div className="text-center">
                        <div className="text-xs text-slate-400">Toplam</div>
                        <div className="font-semibold">{formatTRNumber(parseLocaleNumber(item.yesterday_remaining) + parseLocaleNumber(item.received), 2)}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-amber-600">Tuketim</div>
                        <div className="font-bold text-amber-700">{formatTRNumber(item.consumption || 0, 2)}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-green-600">Maliyet (TL)</div>
                        <div className="font-bold text-green-700">{formatTRNumber(item.total_cost_tl || 0, 2)}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-blue-600">Maliyet (EUR)</div>
                        <div className="font-bold text-blue-700">{formatTRNumber(item.total_cost_eur || 0, 2)}</div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    return (
        <div className="space-y-4 lg:space-y-6" data-testid="bar-stock-page">
            {/* Garson Info Banner */}
            {isGarsonMode && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between" data-testid="garson-banner">
                    <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-800">Garson: {garsonName}</span>
                    </div>
                    <Button variant="outline" size="sm" className="text-xs"
                        onClick={() => { sessionStorage.removeItem('garson_name'); sessionStorage.removeItem('garson_bar'); window.location.href = '/garson-entry'; }}>
                        Degistir
                    </Button>
                </div>
            )}

            {/* Header */}
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                    {(showTreats || showConsumables) && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setShowTreats(false); setShowConsumables(false); }}
                            className="border-slate-300 hover:bg-slate-100 flex-shrink-0"
                            data-testid="back-to-stock-count"
                        >
                            ← Sayıma Dön
                        </Button>
                    )}
                    <div className={`w-10 h-10 lg:w-14 lg:h-14 rounded-xl ${barConfig.color} flex items-center justify-center flex-shrink-0`}>
                        <BarIcon className="w-5 h-5 lg:w-7 lg:h-7 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="font-heading text-lg lg:text-2xl font-bold text-slate-800 truncate">
                            {barConfig.name}
                            {showConsumables && <span className="text-sm font-normal text-slate-500 ml-2">· Sarf Malzemeler</span>}
                            {showTreats && <span className="text-sm font-normal text-slate-500 ml-2">· İkram</span>}
                        </h1>
                        <p className="text-slate-500 text-xs lg:text-sm">Gunluk stok sayim ve takip</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-2 py-1.5 lg:px-3 lg:py-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        <Input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="border-0 bg-transparent p-0 h-auto w-32 lg:w-36 text-sm"
                            data-testid="date-input"
                        />
                    </div>
                    <Button
                        variant={scannerActive ? "default" : "outline"}
                        size="sm"
                        onClick={() => setScannerActive(!scannerActive)}
                        className={scannerActive ? "bg-green-600 hover:bg-green-700 animate-pulse" : ""}
                        data-testid="scanner-toggle-btn"
                    >
                        <ScanBarcode className="w-4 h-4 mr-1" />
                        <span className="hidden sm:inline">{scannerActive ? 'Tarayici Aktif' : 'QR Tarayici'}</span>
                        <span className="sm:hidden">{scannerActive ? 'Aktif' : 'QR'}</span>
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportToPDF} data-testid="export-pdf-btn">
                        <FileText className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">PDF</span>
                    </Button>
                    {isAdmin && (
                        <Button variant="outline" size="sm" onClick={() => setCopyDialogOpen(true)} data-testid="copy-template-btn"
                            className="border-amber-300 text-amber-700 hover:bg-amber-50">
                            <Copy className="w-4 h-4 sm:mr-1" />
                            <span className="hidden sm:inline">Liste Kopyala</span>
                            <span className="sm:hidden">Kopya</span>
                        </Button>
                    )}
                    <Button
                        variant={showConsumables ? "default" : "outline"}
                        size="sm"
                        onClick={() => setShowConsumables(!showConsumables)}
                        data-testid="open-consumables-btn"
                        className={showConsumables ? "bg-slate-700 hover:bg-slate-800" : "border-slate-400 text-slate-700 hover:bg-slate-50"}
                    >
                        <PackagePlus className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Sarf Malzemeler</span>
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/complimentary')}
                        data-testid="open-complimentary-btn"
                        className="border-amber-400 text-amber-700 hover:bg-amber-50"
                    >
                        <CreditCard className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Ödenmez</span>
                    </Button>
                    <Button
                        variant={showTreats ? "default" : "outline"}
                        size="sm"
                        onClick={() => setShowTreats(!showTreats)}
                        data-testid="open-treats-btn"
                        className={showTreats ? "bg-emerald-600 hover:bg-emerald-700" : "border-emerald-400 text-emerald-700 hover:bg-emerald-50"}
                    >
                        <Gift className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">İkram</span>
                    </Button>
                </div>
            </div>

            {/* Sedna "Bugün Otelde" Card */}
            {sednaGuests && sednaGuests.guest_counts ? (
                <Card className="border-l-4 border-l-indigo-500 bg-gradient-to-r from-indigo-50/60 to-white" data-testid="sedna-guests-card">
                    <CardContent className="py-3 px-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                                    <UserCheck className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-semibold">{sednaGuests.report_date === selectedDate ? 'Bugün Otelde' : 'Otelde'}</div>
                                    <div className="text-[10px] text-slate-500">Sedna · {sednaGuests.report_date}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 lg:gap-6">
                                <div className="text-center">
                                    <div className="text-[10px] text-slate-500 uppercase">Yetişkin</div>
                                    <div className="text-lg font-bold text-slate-800" data-testid="sedna-adults">{sednaGuests.guest_counts.adults ?? 0}</div>
                                </div>
                                <div className="text-center">
                                    <div className="text-[10px] text-slate-500 uppercase">Ücretli Çocuk</div>
                                    <div className="text-lg font-bold text-slate-800" data-testid="sedna-children">{sednaGuests.guest_counts.paid_children ?? 0}</div>
                                </div>
                                <div className="text-center border-l border-slate-200 pl-4 lg:pl-6">
                                    <div className="text-[10px] text-slate-500 uppercase">Toplam Misafir</div>
                                    <div className="text-xl font-bold text-indigo-700" data-testid="sedna-total">{sednaGuests.guest_counts.total_guests ?? 0}</div>
                                </div>
                                <div className="text-center hidden sm:block">
                                    <div className="text-[10px] text-slate-500 uppercase">Dolu Oda</div>
                                    <div className="text-lg font-bold text-slate-800" data-testid="sedna-rooms">{sednaGuests.guest_counts.occupied_rooms ?? 0}</div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <Card className="border-l-4 border-l-slate-300 bg-slate-50" data-testid="sedna-guests-card-empty">
                    <CardContent className="py-2.5 px-4 text-sm text-slate-500">
                        <span className="font-semibold text-slate-600">Sedna · {selectedDate}</span> — bu tarihte misafir kaydı yok
                    </CardContent>
                </Card>
            )}

            {/* Sedna Unmatched (çekilen ama tanımlı olmayan) — UI Bildirimi */}
            {unmatchedSedna.length > 0 && (
                <Card className="border-l-4 border-l-amber-500 bg-amber-50/60" data-testid="sedna-unmatched-card">
                    <CardContent className="py-3 px-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div>
                                    <div className="text-sm font-bold text-amber-900">
                                        Sedna&apos;dan çekilen ama bu barda tanımlı olmayan {unmatchedSedna.length} ürün
                                    </div>
                                    <div className="text-xs text-amber-700">Lütfen ürünleri elle ekleyin, eklediklerinizi &quot;Eklendi&quot; ile listeden kaldırın.</div>
                                </div>
                            </div>
                            {unmatchedSedna.length > 1 && (
                                <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100" onClick={dismissAllUnmatched} data-testid="sedna-unmatched-dismiss-all">
                                    Hepsini Kapat
                                </Button>
                            )}
                        </div>
                        <ul className="space-y-1.5 mt-3" data-testid="sedna-unmatched-list">
                            {unmatchedSedna.map((u) => (
                                <li key={u.id} className="flex items-center justify-between gap-3 bg-white border border-amber-200 rounded px-3 py-2 text-sm" data-testid={`sedna-unmatched-item-${u.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-slate-800 truncate">
                                            {u.product_name}
                                            {u.product_code && <span className="ml-2 text-[11px] font-mono text-slate-500">#{u.product_code}</span>}
                                        </div>
                                        <div className="text-xs text-slate-600">
                                            <strong>{Number(u.quantity).toLocaleString('tr-TR')}</strong> {u.unit || ''} — {u.section === 'sarf_malzeme' ? 'Sarf Malzeme' : 'Bar Stok'}
                                            {u.category && <span className="text-slate-400"> · {u.category}</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <Button size="sm" variant="default" className="bg-amber-600 hover:bg-amber-700 text-white h-7 px-2.5" onClick={() => quickAddUnmatched(u)} data-testid={`sedna-unmatched-quick-add-${u.id}`}>
                                            <PackagePlus className="w-3.5 h-3.5 mr-1" /> Hızlı Ekle
                                        </Button>
                                        <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-100 h-7" onClick={() => dismissUnmatched(u.id)} data-testid={`sedna-unmatched-dismiss-${u.id}`}>
                                            Eklendi
                                        </Button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            )}

            {/* Scanner Active Banner + Camera */}
            {scannerActive && (
                <Card className="border-green-300 bg-green-50/50 overflow-hidden" data-testid="scanner-banner">
                    <CardContent className="p-3">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <ScanBarcode className="w-5 h-5 text-green-600 animate-pulse" />
                                <div>
                                    <p className="text-sm font-medium text-green-800">Kamera Tarayici Aktif</p>
                                    <p className="text-xs text-green-600">QR kodu kameraya gosterin</p>
                                </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setScannerActive(false)} data-testid="scanner-close-btn">
                                <X className="w-4 h-4 mr-1" /> Kapat
                            </Button>
                        </div>
                        <div id="qr-reader" className="w-full max-w-sm mx-auto rounded-lg overflow-hidden" style={{ minHeight: '280px' }} />
                    </CardContent>
                </Card>
            )}

            {/* Stock History Panel */}
            {showHistory && (
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Calendar className="w-5 h-5 text-orwion-green" />
                            Stok Gecmisi
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {historyData.length > 0 ? (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-2 p-4">
                                {historyData.map((entry) => {
                                    const isSelected = entry.date === selectedDate;
                                    const dateObj = new Date(entry.date + 'T00:00:00');
                                    const dayName = dateObj.toLocaleDateString('tr-TR', { weekday: 'short' });
                                    const dayNum = dateObj.getDate();
                                    const monthName = dateObj.toLocaleDateString('tr-TR', { month: 'short' });
                                    return (
                                        <button
                                            key={entry.date}
                                            onClick={() => { setSelectedDate(entry.date); setShowHistory(false); }}
                                            className={`p-2 lg:p-3 rounded-lg border text-center transition-all hover:shadow-md ${
                                                isSelected ? 'bg-slate-800 text-white border-slate-800' : 'bg-white hover:bg-slate-50 border-slate-200'
                                            }`}
                                            data-testid={`history-date-${entry.date}`}
                                        >
                                            <div className="text-xs opacity-70">{dayName}</div>
                                            <div className="text-base lg:text-lg font-bold">{dayNum}</div>
                                            <div className="text-xs opacity-70">{monthName}</div>
                                            <div className={`text-xs font-medium mt-1 ${isSelected ? 'text-emerald-300' : 'text-emerald-600'}`}>
                                                {entry.total_tl.toFixed(0)} TL
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="p-6 text-center text-slate-400 text-sm">Henuz kayitli stok verisi yok</div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* İkram (Guest Treats) Panel */}
            {showTreats && (
                <Card className="border-emerald-200 bg-emerald-50/20">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Gift className="w-5 h-5 text-emerald-700" />
                            İkram - {barConfig?.name}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <BarTreats barId={barId} date={selectedDate} />
                    </CardContent>
                </Card>
            )}

            {/* Sarf Malzemeler Panel */}
            {showConsumables && (
                <Card className="border-slate-300 bg-slate-50/30">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <PackagePlus className="w-5 h-5 text-slate-700" />
                            Sarf Malzemeler - {barConfig?.name}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <BarConsumables barId={barId} date={selectedDate} euroRate={euroRate} />
                    </CardContent>
                </Card>
            )}

            {/* Stock Table - hidden when treats/consumables panel is open */}
            {!showTreats && !showConsumables && (
            <div className="block">
                <Card>
                    <CardHeader>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <CardTitle className="text-lg">Stok Sayim Tablosu</CardTitle>
                            <div className="flex items-center gap-2">
                                <Input
                                    type="text"
                                    value={stockSearchTerm}
                                    onChange={(e) => setStockSearchTerm(e.target.value)}
                                    placeholder="Ürün ara..."
                                    className="h-9 w-48"
                                    data-testid="stock-search"
                                />
                                {stockSearchTerm && (
                                    <button onClick={() => setStockSearchTerm('')} className="text-xs text-slate-500 hover:text-slate-800" data-testid="stock-search-clear">Temizle</button>
                                )}
                                {!isFieldLocked && (
                                <Button variant="outline" size="sm" onClick={addNewProduct} data-testid="add-row-btn">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Satir Ekle
                                </Button>
                                )}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <div className="w-full overflow-auto" style={{ WebkitOverflowScrolling: 'touch', maxHeight: 'calc(100vh - 280px)' }}>
                            <div className="relative" style={{ minWidth: '1100px' }}>
                            <table className="w-full caption-bottom text-sm">
                                <thead className="[&_tr]:border-b sticky top-0 z-30 bg-slate-50 shadow-sm">
                                    <tr className="bg-slate-50 border-b transition-colors shadow-sm">
                                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground sticky left-0 bg-slate-50 z-10" style={{ minWidth: '220px', width: '220px' }}>Urun Adi</th>
                                        <th className="h-10 px-2 text-left align-middle font-medium text-muted-foreground" style={{ width: '80px' }}>Birim</th>
                                        <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground" style={{ width: '100px' }}>Dunden Kalan</th>
                                        <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground" style={{ width: '100px' }}>Cekilen Urun</th>
                                        <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground bg-slate-100" style={{ width: '80px' }}>Toplam</th>
                                        <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground" style={{ width: '100px' }}>Bugun Sayim</th>
                                        <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground bg-amber-50" style={{ width: '80px' }}>Tuketim</th>
                                        <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground bg-indigo-50" style={{ width: '110px' }}>Talep</th>
                                        {canSeePrices && <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground" style={{ width: '110px' }}>Birim Fiyat (TL)</th>}
                                        {canSeePrices && <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground bg-green-50" style={{ width: '110px' }}>Toplam (TL)</th>}
                                        {canSeePrices && <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground bg-blue-50" style={{ width: '110px' }}>Toplam (EUR)</th>}
                                        <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground" style={{ width: '110px' }}>İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <SortableContext
                                        items={stockItems.map((it, i) => it._rowId || `row-${i}`)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                    {stockItems.map((item, index) => {
                                        // Search filter: only render rows matching term (always show empty rows so user can type)
                                        if (stockSearchTerm) {
                                            const term = stockSearchTerm.toLocaleLowerCase('tr');
                                            const name = (item.product_name || '').toLocaleLowerCase('tr');
                                            if (name && !name.includes(term)) return null;
                                        }
                                        const hasTodayCount = parseFloat(item.today_count) > 0;
                                        const todayCellClass = isGarsonMode
                                            ? (hasTodayCount ? 'bg-green-100 border-green-300' : 'bg-red-100 border-red-300')
                                            : '';
                                        const rowId = item._rowId || `row-${index}`;
                                        return (
                                        <SortableRow key={rowId} id={rowId} disabled={isFieldLocked}>
                                            {(dragProps) => (
                                            <>
                                            <td className="p-2 align-middle relative sticky left-0 bg-white z-10" style={{ minWidth: '240px', width: '240px' }}>
                                                <div className="flex items-center gap-1">
                                                    {!isFieldLocked && (
                                                        <button
                                                            type="button"
                                                            {...dragProps}
                                                            className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-700 px-1 py-2 flex-shrink-0 touch-none"
                                                            title="Surukleyerek tasi"
                                                            data-testid={`drag-handle-${index}`}
                                                        >
                                                            <GripVertical className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    <div className="flex-1 min-w-0">
                                                <Input value={item.product_name}
                                                    onChange={(e) => handleProductSearch(e.target.value, index, e.target)}
                                                    onFocus={(e) => {
                                                        setActiveInputIndex(index);
                                                        const rect = e.target.getBoundingClientRect();
                                                        const dropdownHeight = 240;
                                                        const spaceBelow = window.innerHeight - rect.bottom;
                                                        const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
                                                        setDropdownPos({
                                                            top: showAbove ? rect.top - dropdownHeight - 2 : rect.bottom + 2,
                                                            left: rect.left,
                                                            width: rect.width
                                                        });
                                                    }}
                                                    placeholder="Urun adi yazin..."
                                                    className="h-9 w-full font-bold text-black"
                                                    disabled={isFieldLocked}
                                                    data-testid={`product-name-${index}`}
                                                />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-2 align-middle">
                                                <Badge variant="outline" className="uppercase text-xs">{item.unit || 'adet'}</Badge>
                                            </td>
                                            <td className="p-2 align-middle">
                                                <Input type="text" inputMode="decimal" value={item.yesterday_remaining ?? ''}
                                                    onChange={(e) => updateItem(index, 'yesterday_remaining', e.target.value)}
                                                    onFocus={(e) => e.target.select()}
                                                    placeholder="0"
                                                    className="h-9 text-center" disabled={isFieldLocked} data-testid={`yesterday-${index}`} />
                                            </td>
                                            <td className="p-2 align-middle">
                                                <Input type="text" inputMode="decimal" value={item.received ?? ''}
                                                    onChange={(e) => updateItem(index, 'received', e.target.value)}
                                                    onFocus={(e) => e.target.select()}
                                                    placeholder="0"
                                                    className="h-9 text-center" disabled={isFieldLocked} data-testid={`received-${index}`} />
                                            </td>
                                            <td className="p-2 align-middle bg-slate-50">
                                                <div className="text-center font-semibold text-slate-700">
                                                    {formatTRNumber(parseLocaleNumber(item.yesterday_remaining) + parseLocaleNumber(item.received), 2)}
                                                </div>
                                            </td>
                                            <td className={`p-2 align-middle ${todayCellClass}`}>
                                                <Input type="text" inputMode="decimal" value={item.today_count ?? ''}
                                                    onChange={(e) => updateItem(index, 'today_count', e.target.value)}
                                                    onFocus={(e) => e.target.select()}
                                                    placeholder="0"
                                                    className={`h-9 text-center font-bold ${isGarsonMode ? (hasTodayCount ? 'border-green-400 bg-green-50 text-green-800' : 'border-red-400 bg-red-50 text-red-800') : ''}`}
                                                    data-testid={`today-count-${index}`} />
                                            </td>
                                            <td className="p-2 align-middle bg-amber-50">
                                                <div className="text-center font-semibold text-amber-700">{formatTRNumber(item.consumption || 0, 2)}</div>
                                            </td>
                                            <td className="p-2 align-middle bg-indigo-50/60" title="Kaydedince Talepler sekmesine + Sedna F&B Talep Fişi'ne iletilir">
                                                <div className="flex items-center gap-1">
                                                    <Input type="text" inputMode="decimal" value={item.request_qty ?? ''}
                                                        onChange={(e) => updateItem(index, 'request_qty', e.target.value)}
                                                        onFocus={(e) => e.target.select()}
                                                        placeholder="0"
                                                        className="h-9 text-center"
                                                        data-testid={`request-qty-${index}`}
                                                        title="Kaydedince Talepler sekmesine + Sedna F&B Talep Fişi'ne iletilir"
                                                    />
                                                    {item.request_sent_at && (
                                                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px] px-1.5 h-6 whitespace-nowrap" data-testid={`request-sent-${index}`} title={`Gönderildi: ${new Date(item.request_sent_at).toLocaleString('tr-TR')}`}>
                                                            ✓
                                                        </Badge>
                                                    )}
                                                </div>
                                            </td>
                                            {canSeePrices && (
                                            <td className="p-2 align-middle">
                                                <Input type="text" inputMode="decimal" value={item.unit_price_tl ?? ''}
                                                    onChange={(e) => updateItem(index, 'unit_price_tl', e.target.value)}
                                                    onFocus={(e) => e.target.select()}
                                                    placeholder="0"
                                                    className={`h-9 text-center ${(parseLocaleNumber(item.unit_price_tl) === 0 && parseLocaleNumber(item.consumption) > 0) ? 'border-red-400 bg-red-50' : ''}`}
                                                    disabled={isFieldLocked} data-testid={`unit-price-${index}`}
                                                    title={(parseLocaleNumber(item.unit_price_tl) === 0 && parseLocaleNumber(item.consumption) > 0) ? 'Birim fiyat girilmemiş — Toplam TL 0 çıkıyor!' : ''}
                                                />
                                            </td>
                                            )}
                                            {canSeePrices && (
                                            <td className={`p-2 align-middle ${(parseLocaleNumber(item.unit_price_tl) === 0 && parseLocaleNumber(item.consumption) > 0) ? 'bg-red-50' : 'bg-green-50'}`}>
                                                <div className={`text-center font-semibold ${(parseLocaleNumber(item.unit_price_tl) === 0 && parseLocaleNumber(item.consumption) > 0) ? 'text-red-600' : 'text-green-700'}`}
                                                    title={(parseLocaleNumber(item.unit_price_tl) === 0 && parseLocaleNumber(item.consumption) > 0) ? 'Birim Fiyat girilmediği için Toplam TL 0. Fiyatı girin.' : ''}>
                                                    {formatTRNumber(item.total_cost_tl || 0, 2)} TL
                                                    {(parseLocaleNumber(item.unit_price_tl) === 0 && parseLocaleNumber(item.consumption) > 0) && (
                                                        <span className="ml-1 text-red-500 font-bold" title="Birim Fiyat eksik!">⚠</span>
                                                    )}
                                                </div>
                                            </td>
                                            )}
                                            {canSeePrices && (
                                            <td className="p-2 align-middle bg-blue-50">
                                                <div className="text-center font-semibold text-blue-700">{formatTRNumber(item.total_cost_eur || 0, 2)} EUR</div>
                                            </td>
                                            )}
                                            <td className="p-2 align-middle">
                                                {!isFieldLocked && (
                                                <div className="flex items-center justify-center gap-0.5">
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-slate-800"
                                                        onClick={() => moveProduct(index, -1)}
                                                        disabled={index === 0}
                                                        title="Yukari tasi"
                                                        data-testid={`move-up-${index}`}>
                                                        <ChevronUp className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-slate-800"
                                                        onClick={() => moveProduct(index, 1)}
                                                        disabled={index === stockItems.length - 1}
                                                        title="Asagi tasi"
                                                        data-testid={`move-down-${index}`}>
                                                        <ChevronDown className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600"
                                                        onClick={() => removeProduct(index)}
                                                        title="Sil"
                                                        data-testid={`remove-row-${index}`}>
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                                )}
                                            </td>
                                            </>
                                            )}
                                        </SortableRow>
                                        );
                                    })}
                                    </SortableContext>
                                </tbody>
                            </table>
                            </div>
                        </div>
                        </DndContext>
                    </CardContent>
                </Card>
            </div>
            )}

            {/* Stock Cards - Mobile (gizlendi - tablo kullanilior) */}
            <div className="hidden">
                <div className="flex items-center justify-between">
                    <h2 className="text-base font-semibold text-slate-800">Stok Sayim ({stockItems.length} urun)</h2>
                    <Button variant="outline" size="sm" onClick={addNewProduct} data-testid="add-row-btn-mobile">
                        <Plus className="w-4 h-4 mr-1" />
                        Ekle
                    </Button>
                </div>
                {stockItems.map((item, index) => (
                    <MobileStockCard key={index} item={item} index={index} />
                ))}
            </div>

            {/* Admin master-data dirty banner */}
            {isAdmin && masterDirty && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="master-dirty-banner">
                    <div className="flex items-center gap-2">
                        <span className="text-amber-700 text-lg">⚠️</span>
                        <div>
                            <div className="text-sm font-semibold text-amber-900">Bekleyen Fiyat / Ürün Değişikliği</div>
                            <div className="text-xs text-amber-700">Otomatik kaydetme duraklatıldı. Onaylamak için &quot;Stok Kaydet&quot; butonuna basın.</div>
                        </div>
                    </div>
                </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end sticky bottom-4 z-10">
                <Button
                    onClick={() => handleSave(false)}
                    disabled={saving}
                    size="lg"
                    className="bg-orwion-green hover:bg-orwion-green-hover px-6 lg:px-8 shadow-lg"
                    data-testid="save-stock-btn"
                >
                    <Save className="w-5 h-5 mr-2" />
                    {saving ? 'Kaydediliyor...' : 'Stok Kaydet'}
                </Button>
            </div>

            {/* Receipt Photos Section */}
            <Card>
                <CardHeader className="py-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Camera className="w-4 h-4 text-slate-500" />
                            <CardTitle className="text-sm font-medium">Siparis Fisleri ({receipts.length})</CardTitle>
                        </div>
                        <div>
                            <input type="file" ref={fileInputRef} onChange={handleReceiptUpload} accept="image/*" className="hidden" />
                            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingReceipt}>
                                <Upload className="w-3 h-3 mr-1" />
                                {uploadingReceipt ? 'Yukleniyor...' : 'Fis Ekle'}
                            </Button>
                        </div>
                    </div>
                </CardHeader>
                {receipts.length > 0 && (
                    <CardContent className="pt-0 pb-3">
                        <div className="flex flex-wrap gap-2">
                            {receipts.map((receipt) => (
                                <div key={receipt.id} className="relative group cursor-pointer"
                                    onClick={() => { setSelectedReceipt(receipt); setReceiptDialogOpen(true); }}>
                                    <img src={`${API_URL}/api/uploads/${receipt.filename}`} alt="Fis"
                                        className="w-16 h-16 object-cover rounded border hover:shadow-md transition-shadow" />
                                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                                        <Eye className="w-4 h-4 text-white" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                )}
            </Card>

            {/* QR Scan Quantity Dialog */}
            <Dialog open={scanDialogOpen} onOpenChange={(open) => { if (!open) cancelScanDialog(); }}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ScanBarcode className="w-5 h-5 text-green-600" />
                            QR ile Urun Ekleme
                        </DialogTitle>
                    </DialogHeader>
                    {scannedProduct && (
                        <div className="space-y-4 py-2">
                            <div className="bg-slate-50 rounded-lg p-4">
                                <p className="text-lg font-bold text-slate-800" data-testid="scanned-product-name">
                                    {scannedProduct.name}
                                </p>
                                <div className="flex items-center gap-2 mt-1">
                                    <Badge variant="outline" className="uppercase">
                                        {scannedProduct.unit || 'adet'}
                                    </Badge>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">
                                    Miktar ({scannedProduct.unit || 'adet'})
                                </label>
                                <Input
                                    type="number"
                                    value={scannedQty}
                                    onChange={(e) => setScannedQty(e.target.value)}
                                    placeholder="Miktar giriniz..."
                                    className="text-xl font-bold text-center h-14"
                                    autoFocus
                                    data-testid="scanned-qty-input"
                                    onKeyDown={(e) => { if (e.key === 'Enter') confirmScannedProduct(); }}
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" className="flex-1" onClick={cancelScanDialog}>
                                    Iptal
                                </Button>
                                <Button
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                    onClick={confirmScannedProduct}
                                    data-testid="confirm-scanned-btn"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Ekle
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Receipt View Dialog */}
            <Dialog open={receiptDialogOpen} onOpenChange={setReceiptDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Fis Fotografi</DialogTitle>
                    </DialogHeader>
                    {selectedReceipt && (
                        <div className="space-y-4">
                            <img src={`${API_URL}/api/uploads/${selectedReceipt.filename}`} alt="Fis"
                                className="w-full max-h-[60vh] object-contain rounded-lg" />
                            <div className="flex justify-between items-center text-sm text-slate-500">
                                <span>Yuklenme: {new Date(selectedReceipt.uploaded_at).toLocaleString('tr-TR')}</span>
                                <Button variant="destructive" size="sm" onClick={() => deleteReceipt(selectedReceipt.id)}>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Sil
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Admin Confirmation Dialog for price/product changes */}
            <Dialog open={adminConfirmOpen} onOpenChange={setAdminConfirmOpen}>
                <DialogContent className="max-w-lg" data-testid="admin-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-700">
                            <span>⚠️</span>
                            Fiyat / Ürün Değişikliği Onayı
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 pt-2">
                        <p className="text-sm text-slate-600">
                            Aşağıdaki ana veri değişikliklerini kaydetmek üzeresiniz. Devam etmek istiyor musunuz?
                        </p>
                        <div className="max-h-60 overflow-auto bg-slate-50 rounded-lg border divide-y">
                            {adminConfirmChanges.map((c, i) => (
                                <div key={i} className="p-2.5 text-sm" data-testid={`admin-change-${i}`}>
                                    <div className="font-semibold text-slate-800 flex items-center gap-2">
                                        <Badge variant="outline" className="text-[10px]">{c.type === 'name' ? 'Ürün Adı' : 'Birim Fiyat'}</Badge>
                                        <span className="truncate">{c.product || `Satır ${c.index + 1}`}</span>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-1">
                                        <span className="line-through text-red-500">{c.oldVal}</span>
                                        <span className="mx-2">→</span>
                                        <span className="text-emerald-600 font-semibold">{c.newVal}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 pt-2">
                            <Button variant="outline" className="flex-1" onClick={() => setAdminConfirmOpen(false)} data-testid="admin-confirm-cancel">
                                İptal
                            </Button>
                            <Button
                                className="flex-1 bg-amber-600 hover:bg-amber-700"
                                onClick={async () => {
                                    setAdminConfirmOpen(false);
                                    await doSave(false);
                                }}
                                data-testid="admin-confirm-save"
                            >
                                Onayla ve Kaydet
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Template Copy Dialog (admin/manager only) */}
            <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
                <DialogContent className="max-w-md" data-testid="copy-template-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Copy className="w-5 h-5 text-amber-600" />
                            Ürün Listesini Kopyala
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-2">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                            <p className="font-semibold mb-1">Kaynak: {barConfig.name}</p>
                            <p className="text-xs">Sadece ürün listesi (ad, birim, fiyat) kopyalanır. Sayım miktarları sıfır kalır.</p>
                        </div>

                        <div>
                            <label className="text-sm font-medium mb-2 block">Hedef Barlar</label>
                            <div className="grid grid-cols-2 gap-2">
                                {Object.entries(BAR_CONFIG).filter(([id]) => id !== barId).map(([id, cfg]) => (
                                    <label key={id} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-slate-50"
                                        data-testid={`copy-target-${id}`}>
                                        <input
                                            type="checkbox"
                                            checked={copyTargets.includes(id)}
                                            onChange={(e) => {
                                                if (e.target.checked) setCopyTargets([...copyTargets, id]);
                                                else setCopyTargets(copyTargets.filter(t => t !== id));
                                            }}
                                            className="w-4 h-4"
                                        />
                                        <span className="text-sm">{cfg.name}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="flex gap-2 mt-2">
                                <Button variant="ghost" size="sm" onClick={() => setCopyTargets(Object.keys(BAR_CONFIG).filter(id => id !== barId))}>
                                    Tümünü Seç
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setCopyTargets([])}>
                                    Temizle
                                </Button>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium mb-2 block">Kopyalama Modu</label>
                            <div className="space-y-2">
                                <label className="flex items-start gap-2 p-2 border rounded-lg cursor-pointer hover:bg-slate-50">
                                    <input type="radio" checked={copyMode === 'overwrite'} onChange={() => setCopyMode('overwrite')}
                                        className="mt-1" data-testid="copy-mode-overwrite" />
                                    <div>
                                        <div className="text-sm font-medium">Üzerine Yaz</div>
                                        <div className="text-xs text-slate-500">Hedef barın mevcut listesi silinir, sadece kaynaktaki ürünler kalır</div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-2 p-2 border rounded-lg cursor-pointer hover:bg-slate-50">
                                    <input type="radio" checked={copyMode === 'merge'} onChange={() => setCopyMode('merge')}
                                        className="mt-1" data-testid="copy-mode-merge" />
                                    <div>
                                        <div className="text-sm font-medium">Birleştir</div>
                                        <div className="text-xs text-slate-500">Mevcut ürünler korunur, eksik olanlar eklenir</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <label className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-pointer">
                            <input type="checkbox" checked={copyApplyToday} onChange={(e) => setCopyApplyToday(e.target.checked)}
                                className="mt-0.5 w-4 h-4" data-testid="copy-apply-today" />
                            <div>
                                <div className="text-sm font-medium text-blue-900">Bugünkü sayım sayfasına da uygula</div>
                                <div className="text-xs text-blue-700">Hedef barın bugüne ait sayımı boşsa, yeni ürün listesi sıfır miktar olarak otomatik yüklenir (önerilen)</div>
                            </div>
                        </label>

                        <div className="flex gap-2 justify-end pt-2">
                            <Button variant="outline" onClick={() => setCopyDialogOpen(false)} disabled={copyLoading}>
                                İptal
                            </Button>
                            <Button onClick={handleCopyTemplate} disabled={copyLoading || copyTargets.length === 0}
                                className="bg-amber-600 hover:bg-amber-700" data-testid="copy-confirm-btn">
                                {copyLoading ? 'Kopyalanıyor...' : `${copyTargets.length} Bara Kopyala`}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {activeInputIndex !== null && suggestions.length > 0 && (
                <div
                    data-autocomplete-dropdown
                    className="bg-white border rounded-lg shadow-xl max-h-60 overflow-auto"
                    style={{
                        position: 'fixed',
                        top: dropdownPos.top,
                        left: dropdownPos.left,
                        width: Math.max(dropdownPos.width, 240),
                        zIndex: 99999
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    {suggestions.map((product) => (
                        <div key={product.id}
                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer flex justify-between items-center border-b last:border-b-0"
                            onMouseDown={(e) => { e.preventDefault(); selectProduct(activeInputIndex, product); }}>
                            <span className="text-sm font-medium">{product.name}</span>
                            <Badge variant="outline" className="text-xs ml-2">{product.unit}</Badge>
                        </div>
                    ))}
                </div>
            )}

            {/* Hızlı Ekle Dialog */}
            {quickAddTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="quick-add-dialog" onClick={() => setQuickAddTarget(null)}>
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b">
                            <div className="flex items-center gap-2 mb-1">
                                <PackagePlus className="w-5 h-5 text-amber-600" />
                                <h3 className="font-bold text-lg text-slate-800">Hızlı Ekle</h3>
                            </div>
                            <p className="text-xs text-slate-500">
                                Bu ürün Sedna&apos;dan çekilmiş ama bu barda tanımlı değil. Sadece fiyat (ve isterseniz sayım) girin.
                            </p>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="bg-amber-50 rounded p-3 border border-amber-200">
                                <div className="text-sm font-semibold text-slate-800">{quickAddTarget.product_name}</div>
                                <div className="text-xs text-slate-600 mt-1">
                                    Kod: <span className="font-mono">{quickAddTarget.product_code || '—'}</span>
                                    {' · '}Birim: <span className="font-medium">{quickAddTarget.unit}</span>
                                    {' · '}Çekilen: <strong className="text-amber-700">{Number(quickAddTarget.quantity).toLocaleString('tr-TR')}</strong>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-slate-600">Birim Fiyat (TL) <span className="text-red-500">*</span></label>
                                <Input
                                    type="number" step="0.01" value={quickAddPrice}
                                    onChange={(e) => setQuickAddPrice(e.target.value)}
                                    placeholder="örn. 25.50"
                                    data-testid="quick-add-price"
                                    autoFocus
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-slate-600">Dünden Kalan (ops.)</label>
                                    <Input
                                        type="number" step="0.01" value={quickAddYesterday}
                                        onChange={(e) => setQuickAddYesterday(e.target.value)}
                                        placeholder="0"
                                        data-testid="quick-add-yesterday"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-slate-600">Bugün Sayım (ops.)</label>
                                    <Input
                                        type="number" step="0.01" value={quickAddCount}
                                        onChange={(e) => setQuickAddCount(e.target.value)}
                                        placeholder="0"
                                        data-testid="quick-add-count"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="p-4 border-t flex items-center justify-end gap-2 bg-slate-50 rounded-b-xl">
                            <Button variant="outline" onClick={() => setQuickAddTarget(null)} disabled={quickAddSubmitting} data-testid="quick-add-cancel">
                                Vazgeç
                            </Button>
                            <Button onClick={submitQuickAdd} disabled={quickAddSubmitting || !quickAddPrice} className="bg-amber-600 hover:bg-amber-700" data-testid="quick-add-submit">
                                {quickAddSubmitting ? 'Ekleniyor...' : 'Bara Ekle'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BarStock;
